import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { Exposure, Peril, PerilCreateRequest } from '../../../types/policy';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import { getUpdatedExposureObj } from '../util_sharedEndorsementAndRenewal';
import AddPerilModal from './AddEndorsementPerilModal';
import PerilsList from './PerilsList';

interface Props {
	authData: AuthData;
	endorsementData: EndorsementData;
}

/**
 * Peril list for an existing exposure on an endorsement
 */
const ExistingExposurePerils = (props: Props) => {
	const { t } = useTranslation();
	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
	const [removedPeril, setRemovePeril] = React.useState<
		Peril | PerilCreateRequest
	>(null);
	const params = useParams();
	const navigate = useNavigate();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const { endorsementData, authData } = props;

	const { endorsementUpdateObj, productConfig, setLoading, saveFastForm } =
		endorsementData;

	const selectedExposure = endorsementData.selectedExposure as Exposure;

	const exposureRequest = getUpdatedExposureObj(
		endorsementUpdateObj,
		selectedExposure.locator,
	);
	const isInEditMode =
		authData.hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';
	const endPerils = exposureRequest.endPerils
		? [...exposureRequest.endPerils]
		: [];
	const addPerils = exposureRequest.addPerils
		? [...exposureRequest.addPerils]
		: [];

	const allEndorsementPerils = [
		...selectedExposure.perils,
		...addPerils,
	].filter((peril) => {
		return !endPerils.find(
			(endPerilLocator) => endPerilLocator === peril.locator,
		);
	});

	const removePeril = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			const perilLocator = removedPeril.locator;
			const exposureLocator = selectedExposure.locator;
			const perilIsAdded = addPerils.some((peril: PerilCreateRequest) => {
				return peril.locator === perilLocator;
			});
			// peril is either existing or added; send options object accordingly
			const removeOptions = perilIsAdded
				? {
						removeAddedPeril: {
							perilLocator,
							exposureLocator,
							exposureIsAdded: false,
						},
				  }
				: {
						removePeril: { perilLocator, exposureLocator },
				  };
			await saveFastForm(getValues(), dirtyFields, reset, removeOptions);
			successToast('Successfully removed peril');
			navigate(
				`/policy/${params.policyId}/endorsements/${params.endorsementId}/exposures/${params.exposureId}/perils`,
			);
		} catch (error) {
			errorToast('Error removing peril');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(e) => e.name === selectedExposure.name,
	);
	const perilConfigs = exposureConfig.perils;

	const renderActions = () => {
		return (
			<AddPerilModal
				perilConfigs={perilConfigs}
				exposureLocator={selectedExposure.locator}
			/>
		);
	};

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={removedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const openConfirmationModal = (peril: Peril | PerilCreateRequest) => {
		setIsConfirmModalOpen(true);
		setRemovePeril(peril);
	};

	return (
		<>
			{isConfirmModalOpen && renderRemovePerilModal()}
			<StyledTitleContainer>
				<StyledTitle>{t('peril.plural.upper')}</StyledTitle>
			</StyledTitleContainer>
			<hr />
			<StyledSectionContainer>
				<PerilsList
					perils={allEndorsementPerils}
					openConfirmationModal={openConfirmationModal}
				/>
			</StyledSectionContainer>
			{isInEditMode && renderActions()}
		</>
	);
};

export default withEndorsementContext<Props>(ExistingExposurePerils);
